import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";

const questions = [
  {
    question: "What type of product are you building?",
    options: [
      { label: "Web Application (SaaS, Dashboard, Portal)", value: "web", tags: ["React", "Next.js", "Node.js"] },
      { label: "Mobile App (iOS, Android, or Both)", value: "mobile", tags: ["React Native", "Swift", "Kotlin"] },
      { label: "API Platform or Backend Service", value: "api", tags: ["Go", "FastAPI", "Node.js"] },
      { label: "Trading or Financial System", value: "trading", tags: ["Rust", "Python", "TimescaleDB"] },
    ],
  },
  {
    question: "What's your expected scale in 12 months?",
    options: [
      { label: "Hundreds of users", value: "small", tags: ["Vercel", "PostgreSQL", "Redis"] },
      { label: "Thousands of users", value: "medium", tags: ["AWS", "PostgreSQL", "Redis", "Docker"] },
      { label: "Tens of thousands of users", value: "large", tags: ["AWS", "Kubernetes", "Kafka", "Redis"] },
      { label: "Millions of transactions", value: "massive", tags: ["AWS", "Kubernetes", "Kafka", "TimescaleDB"] },
    ],
  },
  {
    question: "Do you need AI/ML capabilities?",
    options: [
      { label: "No AI needed right now", value: "none", tags: [] },
      { label: "Basic AI integration (chatbot, content generation)", value: "basic", tags: ["OpenAI", "LangChain"] },
      { label: "Advanced AI (RAG, custom models, ML pipelines)", value: "advanced", tags: ["Python", "FastAPI", "Pinecone", "TensorFlow"] },
      { label: "AI is core to the product", value: "core", tags: ["Python", "FastAPI", "ML Pipeline", "Vector DB"] },
    ],
  },
  {
    question: "What compliance requirements apply?",
    options: [
      { label: "None / Standard web security", value: "none", tags: [] },
      { label: "GDPR / Data privacy", value: "gdpr", tags: ["Encryption", "Audit Logging"] },
      { label: "Financial regulations (PCI-DSS, PSD2, AML)", value: "financial", tags: ["Vault", "Terraform", "Compliance Engine"] },
      { label: "Multiple regulatory frameworks", value: "multi", tags: ["Vault", "Terraform", "Compliance Engine", "Audit System"] },
    ],
  },
  {
    question: "What's your timeline?",
    options: [
      { label: "ASAP (4-8 weeks)", value: "fast", tags: ["Vercel", "Managed Services"] },
      { label: "Standard (8-16 weeks)", value: "standard", tags: [] },
      { label: "Complex (16-24 weeks)", value: "complex", tags: [] },
      { label: "Ongoing development", value: "ongoing", tags: ["CI/CD", "Monitoring"] },
    ],
  },
];

export default function TechStackQuizPage() {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [finished, setFinished] = useState(false);

  const handleNext = () => {
    if (selectedOption === null) return;
    const newAnswers = [...answers, questions[current].options[selectedOption]];
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (current < questions.length - 1) {
      setCurrent(current + 1);
    } else {
      setFinished(true);
    }
  };

  const handleBack = () => {
    if (current > 0) {
      setCurrent(current - 1);
      setAnswers(answers.slice(0, -1));
      setSelectedOption(null);
    }
  };

  const handleRestart = () => {
    setCurrent(0);
    setAnswers([]);
    setSelectedOption(null);
    setFinished(false);
  };

  // Aggregate recommendations
  const allTags = answers.flatMap((a) => a.tags);
  const tagCounts = allTags.reduce((acc, tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
    return acc;
  }, {});
  const sortedTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([tag]) => tag);

  const primaryStack = sortedTags.slice(0, 6);
  const supportingStack = sortedTags.slice(6);

  return (
    <main data-testid="tech-quiz-page">
      <SEOHead
        title="Tech Stack Quiz — Venury"
        description="Answer 5 quick questions and get a tailored technology stack recommendation for your project."
        url="https://venury.tech/resources/tech-quiz"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-orange" style={{ top: "-20%", right: "-10%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <Link to="/resources" className="inline-flex items-center gap-2 font-mono text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
              <ArrowLeft size={14} /> Resources
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-6">
            <span className="tag-box font-mono text-sm text-orange-bright uppercase tracking-[0.2em]">
              Interactive Tool
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight"
          >
            Tech Stack Quiz
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg text-slate-400 leading-relaxed max-w-xl mt-6"
          >
            Answer {questions.length} quick questions and get a tailored technology stack recommendation for your project.
          </motion.p>
        </div>
      </section>

      {/* Quiz */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!finished ? (
            <div>
              {/* Progress */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-xs text-slate-600">
                    Question {current + 1} of {questions.length}
                  </span>
                  <span className="font-mono text-xs text-teal-bright">
                    {Math.round(((current + 1) / questions.length) * 100)}%
                  </span>
                </div>
                <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-teal-primary rounded-full"
                    animate={{ width: `${((current + 1) / questions.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Question */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8">
                    {questions[current].question}
                  </h2>

                  <div className="space-y-3">
                    {questions[current].options.map((option, i) => (
                      <button
                        key={i}
                        data-testid={`quiz-option-${i}`}
                        onClick={() => setSelectedOption(i)}
                        className={`w-full p-5 rounded-lg border text-left transition-all duration-200 flex items-center gap-4 ${
                          selectedOption === i
                            ? "border-teal-primary bg-teal-primary/10"
                            : "border-white/5 hover:border-white/20"
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                          selectedOption === i ? "border-teal-bright bg-teal-primary" : "border-white/20"
                        }`}>
                          {selectedOption === i && <Check size={14} className="text-white" />}
                        </div>
                        <span className={`text-sm transition-colors duration-200 ${
                          selectedOption === i ? "text-white font-semibold" : "text-slate-400"
                        }`}>
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-10">
                <button
                  onClick={handleBack}
                  disabled={current === 0}
                  className={`inline-flex items-center gap-2 font-mono text-sm transition-colors duration-200 ${
                    current === 0 ? "text-slate-700 cursor-not-allowed" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  data-testid="quiz-next-btn"
                  onClick={handleNext}
                  disabled={selectedOption === null}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    selectedOption !== null
                      ? "bg-teal-primary hover:bg-teal-dark text-white"
                      : "bg-white/5 text-slate-600 cursor-not-allowed"
                  }`}
                >
                  {current === questions.length - 1 ? "See Results" : "Next"} <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ) : (
            /* Results */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-12">
                <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em] mb-6 inline-flex">
                  Your Results
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mt-4 mb-4">
                  Recommended Tech Stack
                </h2>
                <p className="text-base text-slate-400 max-w-lg mx-auto">
                  Based on your requirements, here's the technology stack our team would recommend.
                </p>
              </div>

              {primaryStack.length > 0 && (
                <div className="glass-card rounded-lg p-8 mb-8">
                  <h3 className="font-mono text-xs text-teal-bright uppercase tracking-widest mb-6">Primary Technologies</h3>
                  <div className="flex flex-wrap gap-3">
                    {primaryStack.map((tech) => (
                      <span key={tech} className="px-4 py-2.5 rounded-lg bg-teal-primary/10 border border-teal-primary/20 text-teal-bright font-mono text-sm font-semibold">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {supportingStack.length > 0 && (
                <div className="glass-card rounded-lg p-8 mb-8">
                  <h3 className="font-mono text-xs text-orange-bright uppercase tracking-widest mb-6">Supporting Technologies</h3>
                  <div className="flex flex-wrap gap-3">
                    {supportingStack.map((tech) => (
                      <span key={tech} className="px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 font-mono text-sm">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <Link
                  to="/contact"
                  data-testid="quiz-cta"
                  className="flex-1 py-4 bg-teal-primary hover:bg-teal-dark text-white font-bold rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  Discuss This Stack <ArrowUpRight size={16} />
                </Link>
                <button
                  data-testid="quiz-restart-btn"
                  onClick={handleRestart}
                  className="flex-1 py-4 border border-white/10 hover:border-white/30 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Retake Quiz
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
}
